"use client";

import { useEffect, useState } from "react";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

import { sourceFields } from "@/constants/source-fields";
import { type KeywordProfile } from "@/hooks/use-keyword-profiles";
import { useKeywordProfilesContext as useKeywordProfiles } from '@/context/keyword-profiles-context'
import Spinner from "./spinner/spinner";

export default function KeywordProfileForm({
    profileId,
    onClose,
}: {
    profileId: string | null;
    onClose: () => void;
}) {
    const { profiles, updateProfile, createProfile, setActiveProfileId } = useKeywordProfiles();

    const [local, setLocal] = useState<KeywordProfile | null>(null);
    const [keywordInput, setKeywordInput] = useState<string>("");
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!profileId) {
            setLocal(null);
            return;
        }

        const found = profiles.find((p) => p.id === profileId) ?? null;
        setLocal(found);
    }, [profileId, profiles]);

    const createEmptyProfile = (source: KeywordProfile["source"]) => {
        return {
            id: crypto.randomUUID(),
            source,
            name: `Untitled ${source.toUpperCase()} Profile`,
            keywords: [],
            settings: {},
        } satisfies KeywordProfile;
    };

    const save = async () => {
        if (!local) return;

        setIsSaving(true);

        // mock delay
        await new Promise((r) => setTimeout(r, 800));

        if (profileId) {
            updateProfile(local.id, local);
        } else {
            const newProfile = createProfile(local.source);
            updateProfile(newProfile.id, local);
            setActiveProfileId(newProfile.id);
        }

        setIsSaving(false);
        onClose();
    };

    const addKeyword = () => {
        const trimmed = keywordInput.trim();
        if (!trimmed) return;

        setLocal((prev) =>
            prev
                ? { ...prev, keywords: Array.from(new Set([...prev.keywords, trimmed])) }
                : prev
        );
        setKeywordInput("");
    };

    const removeKeyword = (k: string) => {
        setLocal((prev) =>
            prev ? { ...prev, keywords: prev.keywords.filter((x) => x !== k) } : prev
        );
    };

    if (!local) {
        return (
            <div className="p-4 border border-dashed border-primary/20 bg-background/40">
                <p className="text-sm font-mono text-muted-foreground">
                    {profileId ? "Profile not found." : "Create a new profile."}
                </p>

                <div className="mt-3 flex flex-col gap-y-2">
                    <label className="text-xs font-mono text-muted-foreground">
                        Choose Source
                    </label>

                    <Select
                        onValueChange={(val) => {
                            const p = createEmptyProfile(val as any);
                            setLocal(p);
                        }}
                    >
                        <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select source..." />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="x">X / Twitter</SelectItem>
                            <SelectItem value="youtube">YouTube</SelectItem>
                            {/* <SelectItem value="ecommerce">E-commerce</SelectItem> */}
                        </SelectContent>
                    </Select>
                </div>
            </div>
        );
    }

    const fields = sourceFields[local.source];

    return (
        <div className="p-4 border border-dashed border-primary/20 bg-background/40">
            <div className="flex items-center justify-between">
                <h2 className="text-sm font-mono tracking-wide text-primary">
                    Keyword Profile
                </h2>
                <Button onClick={save} size="sm" disabled={isSaving} className="font-mono flex flex-row items-center">
                    {isSaving ? <Spinner size={14} /> : null}
                    {isSaving ? "Saving..." : "Save Profile"}
                </Button>
            </div>

            <div className="mt-3 space-y-3">
                {/* NAME */}
                <div>
                    <label className="text-xs font-mono text-muted-foreground">
                        Profile Name
                    </label>
                    <Input
                        className="w-full mt-1 px-3 py-2 border border-primary/20 rounded-lg bg-background/30 text-primary"
                        value={local.name}
                        onChange={(e) => setLocal({ ...local, name: e.target.value })}
                    />
                </div>

                {/* KEYWORDS */}
                <div>
                    <label className="text-xs font-mono text-muted-foreground">
                        Keywords
                    </label>
                    <div className="flex gap-2 mt-1">
                        <Input
                            className="flex-1 px-3 py-2 border border-primary/20 rounded-lg bg-background/30 text-primary"
                            value={keywordInput}
                            onChange={(e) => setKeywordInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") addKeyword();
                            }}
                            placeholder="Type keyword then press Enter"
                        />
                        <button
                            className="px-3 py-2 border text-primary text-sm border-primary/20 rounded-lg hover:bg-primary/10 transition font-mono"
                            onClick={addKeyword}
                        >
                            Add
                        </button>
                    </div>

                    <div className="flex flex-wrap gap-2 mt-2">
                        {local.keywords.map((k) => (
                            <span
                                key={k}
                                className="flex items-center gap-2 px-3 py-1 text-primary border border-primary/20 rounded-full text-xs font-mono bg-background/30"
                            >
                                {k}
                                <button
                                    className="text-xs text-red-400 hover:text-red-500"
                                    onClick={() => removeKeyword(k)}
                                >
                                    x
                                </button>
                            </span>
                        ))}
                    </div>
                </div>

                {/* DYNAMIC FIELDS */}
                <div className="space-y-3">
                    {fields.map((f) => (
                        <div key={f.key}>
                            <label className="text-xs font-mono text-muted-foreground">
                                {f.label}
                            </label>

                            {f.type === "select" ? (
                                <Select
                                    value={local.settings[f.key] ?? ""}
                                    onValueChange={(val) =>
                                        setLocal({
                                            ...local,
                                            settings: { ...local.settings, [f.key]: val },
                                        })
                                    }
                                >
                                    <SelectTrigger className="w-full mt-1">
                                        <SelectValue placeholder="Select..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Select</SelectItem>
                                        {(f as any).options?.map((o: string) => (
                                            <SelectItem key={o} value={o}>
                                                {o}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            ) : (
                                <Input
                                    className="w-full mt-1 px-3 py-2 border border-primary/20 rounded-lg bg-background/30 text-primary"
                                    type={f.type}
                                    value={local.settings[f.key] ?? ""}
                                    onChange={(e) =>
                                        setLocal({
                                            ...local,
                                            settings: { ...local.settings, [f.key]: e.target.value },
                                        })
                                    }
                                />
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
